import { useEffect, useState } from "react";
// import QuizClockIcon from "../components/Icons/QuizClockIcon";
import CheckIcon from "../components/Icons/CheckIcon";
import QuizPage from "../components/Quiz/QuizPage";
import QuizXIcon from "../components/Icons/QuizXIcon";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useUserDataStore } from "../stores/user";
import { useQuery } from "@tanstack/react-query";
import Spinner from "../components/Shared/Spinner";
import { GetQuizById } from "../api/Quiz/GetQuizById";
import QuizClockIcon from "../components/Icons/QuizClockIcon";
import { axios } from "../lib/axios";
import PastAttempts from "../components/Quiz/PastAttempts";
import { GetCBQByQuiz } from "../api/Quiz/GetCBQByQuiz";
import MergedMcqCbqExam from "./MergedMcqCbqExam";

const Quiz = () => {
  const [SearchParams] = useSearchParams();
  const token = SearchParams.get("token");
  const [isFetching, setIsFetching] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const { userData, setUserData } = useUserDataStore();
  const handleConfirmClick = () => {
    setIsConfirmed(true);
  };
  const { id } = useParams();
  const quizId = id || "";

  const navigate = useNavigate();
  const { data: QuizByIdDTO, isLoading: isQuizLoading } = useQuery({
    queryFn: () => GetQuizById(quizId),
    queryKey: ["quiz", quizId],
    refetchInterval: 0,
    // See Exam.tsx: any refetch mid-exam can reorder questions under answers
    // that are tracked by index.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
  const Quiz = QuizByIdDTO?.data;

  // A linked CBQ exam turns this MCQ quiz into a two-section merged exam.
  const { data: cbqLink } = useQuery({
    queryFn: () => GetCBQByQuiz(quizId),
    queryKey: ["cbq-by-quiz", quizId],
    enabled: !!quizId,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  // When on, a quiz with a published linked CBQ exam runs the merged
  // MCQ+CBQ flow instead of the plain MCQ flow.
  const CBQ_MERGE_ENABLED = true;
  // Pick a published linked CBQ exam (gated by this quiz).
  const linkedCbqExam = CBQ_MERGE_ENABLED
    ? (cbqLink?.data ?? []).find((e) => !!e.publishedAt)
    : undefined;
  const cbqRequiredScore =
    linkedCbqExam?.quizGate?.requiredScore ??
    linkedCbqExam?.requiredQuizScore ??
    0;

  const getUserData = async (token: string) => {
    setIsFetching(true);
    const { data } = await axios.get(`/student/profile`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (data) setUserData(data);
    setIsFetching(false);
  };

  useEffect(() => {
    if (token) {
      getUserData(token);
    }
  }, [token]);

  if (isQuizLoading || isFetching)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  const totalMinutes = Quiz?.questions ? Quiz?.questions?.length * 2 || 0 : 0;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const seconds = 0;

  const formattedHours = hours.toString().padStart(2, "0");
  const formattedMinutes = minutes.toString().padStart(2, "0");
  const formattedSeconds = seconds.toString().padStart(2, "0");

  const quizClockText = `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;

  return (
    <div className=" min-h-screen flex flex-col justify-center items-center bg-[#dfe7eb]">
      {isConfirmed ? (
        linkedCbqExam && Quiz ? (
          <MergedMcqCbqExam
            quiz={Quiz}
            cbqExamId={linkedCbqExam.id}
            requiredScore={cbqRequiredScore}
          />
        ) : (
          <div className="w-full">
            <QuizPage
              firstName={`${userData?.firstName}`}
              lastName={`${userData?.lastName}`}
              quizData={Quiz}
            />
          </div>
        )
      ) : (
        <div className="px-4 sm:w-fit w-full mx-auto">
          <div className="sm:w-[486px]  w-full shadow-quizShadow bg-white rounded overflow-hidden pb-16">
            <div className="h-11 bg-[#4d4c4d] flex justify-between items-center px-2.5 text-white">
              <p className="font-semibold">Confirm Details</p>
              <div className="flex items-center gap-1">
                <QuizClockIcon />
                {quizClockText}
              </div>
            </div>
            <div className="text-[#333333] mt-12 sm:mx-0 mx-5">
              <div className="bg-[#f8f9fa] border rounded border-[#B3B3B3] sm:w-[372px] mx-auto py-[18px] sm:px-6 px-3">
                <div className="flex sm:gap-8 gap-4">
                  <div>
                    <p>First Name:</p>
                    <p>Last Name:</p>
                    <p>Exam Name:</p>
                  </div>
                  <div>
                    <p> {userData?.firstName}</p>
                    <p> {userData?.lastName}</p>
                    <p>{Quiz?.title}</p>
                  </div>
                </div>
              </div>
              <p className="text-center py-8">Are the details above correct?</p>
              <div className="flex justify-center gap-4">
                <button
                  className="bg-[#8cc63f] text-white flex items-center justify-center h-10 w-32 rounded gap-3"
                  onClick={handleConfirmClick}
                >
                  <CheckIcon /> Confirm
                </button>
                <button
                  onClick={() => navigate(-1)}
                  className="bg-[#8cc63f] text-white flex items-center justify-center h-10 w-24 rounded gap-2"
                >
                  <QuizXIcon /> Cancel
                </button>
              </div>
              {quizId && (
                <div className="sm:mx-6 mx-5 mt-6">
                  <PastAttempts quizId={quizId} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Quiz;
